// The provider door, as a browser walks it (#820; ADR-0103 decisions 5 and 9).
//
// `GET /auth/doors` names the identity provider arpIQ runs and the public
// client Zenith is registered as there. The page sends the person to that
// provider's hosted login with the authorization code flow and PKCE; the
// provider sends them back to CALLBACK_PATH with a code; the code is traded at
// the provider's token endpoint for an ID token; and the ID token goes to
// `POST /auth/sessions` **once** and is dropped. Nothing here keeps a token:
// what outlives the redirect is the state and the PKCE verifier, in this tab's
// sessionStorage, and they are removed on the way back whatever the outcome.
//
// **The two calls to the provider are not calls to a private API of Zenith's.**
// The provider is a third party the browser is sent to by design — decision 5
// puts discovery on its hosted login — and a public client has no other way to
// redeem its code: the SPA holds no secret, so no server can redeem it on the
// SPA's behalf without being handed the verifier, which would make it a second
// door. Everything Zenith answers is `/v1`, through the generated client.
//
// **Every way the return can fail is one outcome** — undefined — so the page
// has one sentence for all of them: a state that does not match, a person who
// cancelled at the provider, a code the provider will not redeem. None of them
// is something the person can act on differently, and none is Zenith's
// §6.3 question, which the exchange answers with its own single refusal.
import { api } from "../api/client";
import type { components } from "../api/schema";

export type SignInProvider = components["schemas"]["SignInProvider"];

/** Where the provider sends the browser back. The shell serves the bundle at
 * any path, and an anonymous caller is always shown the sign-in page, so this
 * is a path the page recognises rather than a route. */
export const CALLBACK_PATH = "/auth/callback";

const KEY = "zenith:provider-sign-in";

interface Pending {
  state: string;
  verifier: string;
  client_id: string;
  token_endpoint: string;
  redirect_uri: string;
  return_to: string;
  /** Why the flow started: to sign in, or — for a person already signed in —
   * to link the identity to their own user (ADR-0162; #974), or to spend an
   * identity link a person holding the server issued (ADR-0182; #1034). */
  intent?: Intent;
  /** The identity link's token, for a `claim` only. It is a credential, and
   * it is here for the one reason the verifier is: it has to outlive the
   * redirect to the provider. It lives in this tab alone and is removed on the
   * way back, whatever the outcome. */
  claim?: string;
}

/** What a return from the provider is for. A sign-in exchanges the token for a
 * session; a link hands it to `POST /me/identities` with the password; a claim
 * hands it, with the identity link's token, to `/auth/identity-link`. */
export type Intent = "sign-in" | "link" | "claim";

interface Discovery {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
}

/** The provider did not answer its discovery document, or answered one that
 * is not its own. An outage, not a refusal: nothing about the person. */
export class ProviderUnavailable extends Error {
  constructor() { super("the identity provider did not answer"); }
}

async function discover(issuer: string): Promise<Discovery> {
  let d: Partial<Discovery>;
  try {
    const r = await globalThis.fetch(issuer.replace(/\/+$/, "") + "/.well-known/openid-configuration");
    if (!r.ok) throw new ProviderUnavailable();
    d = (await r.json()) as Partial<Discovery>;
  } catch {
    throw new ProviderUnavailable();
  }
  // A document naming another issuer is not this provider's, and the
  // endpoints it names are not ones to send a person to.
  if (d.issuer !== issuer || !d.authorization_endpoint || !d.token_endpoint) throw new ProviderUnavailable();
  return d as Discovery;
}

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function random(n: number): string {
  return base64url(crypto.getRandomValues(new Uint8Array(n)));
}

async function s256(verifier: string): Promise<string> {
  return base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
}

/** Where the browser is sent. An object so a test can replace the one act
 * jsdom cannot perform — leaving the page — without touching `location`. */
export const browser = { assign: (url: string) => window.location.assign(url) };

/** A path on this origin to come back to, or "/". Anything that could leave
 * the origin — a scheme, `//host` — is not a place a sign-in returns to. */
export function safeReturn(to: string | undefined): string {
  if (!to || !to.startsWith("/") || to.startsWith("//") || to.startsWith("/\\") || to.startsWith(CALLBACK_PATH)) return "/";
  return to;
}

/** The address to send the browser to, having remembered what the return
 * needs. `returnTo` is where the person was headed.
 *
 * **A link asks the provider for a fresh sign-in** (`prompt=login`), so the
 * person picks the account they mean rather than whichever one the browser
 * already holds. It is a request and not a guarantee: ZITADEL's API was
 * measured to finish an auth request with an old session whatever `prompt`
 * says (#974), so nothing here relies on it — the password re-entered on the
 * way back is what re-proves the session (ADR-0162 decision 2b).
 *
 * **A sign-in after Switch user asks for one too** (`fresh`; #2054, ruled
 * 2026-10-27). The previous person's session at the provider outlives Zenith's:
 * measured on the laptop stack (ZITADEL v4.16.0, login UI driven), a hint
 * naming somebody else lands on the provider's account chooser listing the
 * previous person, and no hint finishes silently as them; with `prompt=login`
 * the login opens the named person's password page, or asks for a login name
 * where there is no hint. */
export async function beginSignIn(p: SignInProvider, returnTo: string, intent: Intent = "sign-in", claim?: string, fresh = false): Promise<string> {
  const { pending, authorization_endpoint } = await remember(p, returnTo, intent, claim);
  const q = new URLSearchParams({
    client_id: p.client_id,
    redirect_uri: pending.redirect_uri,
    response_type: "code",
    scope: p.scopes.join(" "),
    state: pending.state,
    code_challenge: await s256(pending.verifier),
    code_challenge_method: "S256",
  });
  // A claim asks for a fresh sign-in too, for the same reason and with the
  // same caveat: the server refuses a provider address that is not the
  // user's, which is what does not depend on the provider honouring it.
  if (intent === "link" || intent === "claim" || fresh) q.set("prompt", "login");
  // A hosted person found by their address (ADR-0183 decision 2f): the
  // provider's login opens on that account rather than asking again.
  if (p.login_hint) q.set("login_hint", p.login_hint);
  return authorization_endpoint + "?" + q.toString();
}

/** The invitation cannot be accepted: expired, revoked, accepted already, or
 * never issued — one refusal, for ZENITH.md §6.3's reason. */
export class AcceptRefused extends Error {
  constructor() { super("the invitation cannot be accepted"); }
}

/** The organization the invitee would be hosted in is not made yet
 * (`provider_org_pending`, ADR-0171 decision 8a) — an outage of setup, which
 * the invitee can only wait out. */
export class AcceptPending extends Error {
  constructor() { super("the invitation's organization at the provider is not ready"); }
}

/** The address an acceptance sends the browser to next (ADR-0174; #1013).
 *
 * **Zenith opens the code flow, not the page**, because the scope decides the
 * door and the page is not told which scope an invitation is for: the first
 * call carries the browser's own challenge and state, and Zenith answers with
 * where to go — the provider's set-password step for an account it made, or a
 * sign-in. The verifier stays in this tab's storage exactly as on a sign-in,
 * and the return is finished by the same callback. The address may carry a
 * one-time code; it is followed, never stored. */
export async function beginAccept(p: SignInProvider, token: string, returnTo: string): Promise<string> {
  const { pending } = await remember(p, returnTo, "sign-in");
  const { data, response } = await api.POST("/invitations:accept", { body: { token, hosted: {
    redirect_uri: pending.redirect_uri, code_challenge: await s256(pending.verifier), state: pending.state } } });
  if (response.status === 200 && data && "next" in data && typeof data.next === "string") return data.next;
  take();
  if (response.status === 404) throw new AcceptRefused();
  if (response.status === 409) throw new AcceptPending();
  throw new ProviderUnavailable();
}

/** What a start leaves for its return: the state and verifier, in this tab. */
async function remember(p: SignInProvider, returnTo: string, intent: Intent, claim?: string): Promise<{ pending: Pending; authorization_endpoint: string }> {
  const d = await discover(p.issuer);
  const pending: Pending = {
    state: random(32),
    verifier: random(48),
    client_id: p.client_id,
    token_endpoint: d.token_endpoint,
    redirect_uri: window.location.origin + CALLBACK_PATH,
    return_to: safeReturn(returnTo),
    intent,
    ...(claim ? { claim } : {}),
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(pending));
  } catch {
    // Storage refused (a private window with it blocked): there would be
    // nothing to check the return against, so the flow does not start.
    throw new ProviderUnavailable();
  }
  return { pending, authorization_endpoint: d.authorization_endpoint };
}

/** What the start remembered, removed as it is read: a return is finished
 * once, and a reload of the callback address is a new sign-in. */
function take(): Pending | undefined {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return raw ? (JSON.parse(raw) as Pending) : undefined;
  } catch {
    return undefined;
  }
}

/** Finishes the return the provider sent: the ID token to exchange and where
 * to go afterwards, or undefined for every way it cannot finish. */
export async function finishSignIn(search: string): Promise<{ idToken: string; returnTo: string; intent: Intent; claim?: string } | undefined> {
  const pending = take();
  const q = new URLSearchParams(search);
  const code = q.get("code");
  if (!pending || !code || q.get("error") || q.get("state") !== pending.state) return undefined;
  try {
    const r = await globalThis.fetch(pending.token_endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: pending.redirect_uri,
        client_id: pending.client_id,
        code_verifier: pending.verifier,
      }).toString(),
    });
    if (!r.ok) return undefined;
    const t = (await r.json()) as { id_token?: unknown };
    if (typeof t.id_token !== "string" || t.id_token === "") return undefined;
    return { idToken: t.id_token, returnTo: pending.return_to, intent: pending.intent ?? "sign-in", claim: pending.claim };
  } catch {
    return undefined;
  }
}
